import { motion } from 'framer-motion';
import type { IUser } from '@tambola/shared';

interface HeaderProps {
  user: IUser | null;
  onOpenSettings: () => void;
  onLogout?: () => void;
}

export function Header({ user, onOpenSettings, onLogout }: HeaderProps) {
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Welcome Back';
    return 'Good Evening';
  };

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="w-full flex items-center justify-between px-6 py-4 backdrop-blur-md border-b border-white/5 bg-slate-950/20 z-30"
    >
      {/* Brand logo (Left side) */}
      <div className="flex items-center space-x-2">
        <div className="flex flex-col">
          <span 
            style={{ textShadow: '0 0 10px rgba(124,58,237,0.4)' }}
            className="font-display text-lg font-black tracking-wider text-white"
          >
            TAMBOLA
          </span>
          <span className="text-[10px] font-bold tracking-widest text-gradient uppercase">
            ONLINE
          </span>
        </div>
      </div>

      {/* Greeting (Center) */}
      <div className="hidden md:flex flex-col items-center">
        <span className="text-xs text-surface-400 font-medium tracking-wide uppercase">
          {getGreeting()}
        </span>
        <span className="text-sm font-bold text-amber-200 tracking-wide">
          {user ? user.displayName : 'Player'}
        </span>
      </div>

      {/* Profile Widget & Utilities (Right side) */}
      <div className="flex items-center space-x-4">
        {/* Settings */}
        <button
          onClick={onOpenSettings}
          className="w-8 h-8 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 flex items-center justify-center text-surface-300 hover:text-white transition-all cursor-pointer"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>

        {/* Logout Button (Symbol Only) */}
        {onLogout && (
          <button
            onClick={onLogout}
            title="Log Out"
            className="w-8 h-8 rounded-full bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 flex items-center justify-center text-red-400 hover:text-red-300 transition-all cursor-pointer"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        )}

        {/* User Card info */}
        {user && (
          <div className="flex items-center space-x-2 pl-2 border-l border-white/10">
            <span className="text-xs font-semibold text-white/90 hidden sm:inline-block tracking-wide">
              {user.displayName}
            </span>
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-purple-600 to-pink-500 flex items-center justify-center text-xs font-black shadow-glow">
              {user.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt="avatar"
                  className="w-full h-full rounded-full"
                />
              ) : (
                user.displayName.charAt(0).toUpperCase()
              )}
            </div>
          </div>
        )}
      </div>
    </motion.header>
  );
}
